// contentFeedSaga.js
import { takeLatest, call, put, delay, race, take, select } from "redux-saga/effects";
import {
  GET_CONTENT_FEED_REQUEST,
  getContentFeedSuccess,
  getContentFeedFailure,
  REFRESH_CONTENT_FEED_REQUEST,
  refreshContentFeedSuccess,
  refreshContentFeedFailure,
  SEARCH_CONTENT_FEED_REQUEST,
  searchContentFeedSuccess,
  searchContentFeedFailure,
  refreshContentFeedRequest,
  clearContentFeedRefresh,
} from "../actions/contentFeedActions";
import { getContentFeedGeneratedAt } from "../getters/contentFeedGetters";
import {
  getContentFeedFromApi,
  refreshContentFeedFromApi,
  searchContentFeedFromApi,
} from "../apis/contentFeedApi";
import { getTenantId } from "../utils/authUtils";

function* getContentFeedSaga(action) {
  try {
    const tenantId = getTenantId();
    const { limit, nextToken, append, categoryFilter } = action.payload || {};
    const result = yield call(getContentFeedFromApi, {
      operationType: "GET_CONTENT_FEED",
      tenantId,
      limit: limit || 20,
      ...(nextToken ? { nextToken } : {}),
      ...(categoryFilter ? { categoryFilter } : {}),
    });
    yield put(getContentFeedSuccess({ ...(result || { items: [], generatedAt: null, totalItems: 0 }), append: !!append }));
    // Auto-queue a refresh only when the snapshot itself ("all") is empty —
    // a category-filtered query may legitimately return zero without needing rebuild.
    if (!result?.items?.length && (!categoryFilter || categoryFilter === "all")) {
      yield put(refreshContentFeedRequest({ tenantId, limit: limit || 20 }));
    }
  } catch (error) {
    const errorMessage = error?.message || error?.toString() || "Unknown error";
    console.error("contentFeedSaga - getContentFeedSaga error:", errorMessage);
    yield put(getContentFeedFailure(errorMessage));
  }
}

function* pollForSnapshot({ tenantId, limit, prevGeneratedAt }) {
  const MAX_POLLS = 10;
  const POLL_INTERVAL_MS = 6000;
  for (let i = 0; i < MAX_POLLS; i++) {
    const { cancelled } = yield race({
      waited: delay(POLL_INTERVAL_MS),
      cancelled: take(REFRESH_CONTENT_FEED_REQUEST),
    });
    if (cancelled) return; // new refresh queued — let that saga own the status
    try {
      const result = yield call(getContentFeedFromApi, {
        operationType: "GET_CONTENT_FEED",
        tenantId,
        limit,
      });
      // Only treat as updated if generatedAt changed from what we had before refresh
      if (result?.generatedAt && result.generatedAt !== prevGeneratedAt) {
        yield put(getContentFeedSuccess({ ...result, append: false }));
        yield put(clearContentFeedRefresh());
        return;
      }
    } catch {
      // swallow poll errors — keep trying
    }
  }
  // Timed out without a new snapshot — clear the QUEUED banner
  yield put(clearContentFeedRefresh());
}

function* refreshContentFeedSaga(action) {
  try {
    const tenantId = action.payload?.tenantId || getTenantId();
    const limit = action.payload?.limit || 20;
    const prevGeneratedAt = yield select(getContentFeedGeneratedAt);
    const result = yield call(refreshContentFeedFromApi, {
      operationType: "REFRESH_CONTENT_FEED",
      tenantId,
      limit,
    });
    yield put(refreshContentFeedSuccess(result));
    // Poll for the updated snapshot — compare generatedAt so we don't exit early on stale data
    yield call(pollForSnapshot, { tenantId, limit, prevGeneratedAt });
  } catch (error) {
    const errorMessage = error?.message || error?.toString() || "Unknown error";
    console.error("contentFeedSaga - refreshContentFeedSaga error:", errorMessage);
    yield put(refreshContentFeedFailure(errorMessage));
  }
}

function* searchContentFeedSaga(action) {
  try {
    const tenantId = getTenantId();
    const { keyword, limit, categoryFilter } = action.payload;
    const result = yield call(searchContentFeedFromApi, {
      operationType: "SEARCH_CONTENT_FEED",
      tenantId,
      keyword,
      limit: limit || 20,
      ...(categoryFilter ? { categoryFilter } : {}),
    });
    yield put(searchContentFeedSuccess(result || { items: [], totalItems: 0 }));
  } catch (error) {
    const errorMessage = error?.message || error?.toString() || "Unknown error";
    console.error("contentFeedSaga - searchContentFeedSaga error:", errorMessage);
    yield put(searchContentFeedFailure(errorMessage));
  }
}

function* watchContentFeedActions() {
  yield takeLatest(GET_CONTENT_FEED_REQUEST, getContentFeedSaga);
  yield takeLatest(REFRESH_CONTENT_FEED_REQUEST, refreshContentFeedSaga);
  yield takeLatest(SEARCH_CONTENT_FEED_REQUEST, searchContentFeedSaga);
}

export default watchContentFeedActions;
